import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import DataApi from 'api/faber/DataApi'
import MenuClose from 'assets/icons/MenuClose.png'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import { isWithinInterval } from 'date-fns'
import addMonths from 'date-fns/addMonths'
import format from 'date-fns/format'
import OrbitSession from 'hooks/useCookies'
import {
  DataCDROverviewType,
  DataCDRPackageDetailType
} from 'types/api/faber/dataUsage.type'
import { FinanceTableRows } from 'types/api/faber/payment.type'
import { DataRolloverRows } from 'types/api/faber/subscription.type'

import { DataAllocationLog, DetailsPaneItem } from '../components'

const FaberCDRDialog = ({
  handleDialogClose,
  rolloverDialog,
  handleMenuClose,
  row
}: FaberDataRolloverDialogProps) => {
  const faberToken = OrbitSession.get('faberToken')
  const [cycleInterval, setCycleInterval] = useState('')

  const handleChange = (event) => {
    setCycleInterval(event.target.value as string)
  }

  const [overviewRows, setOverviewRows] = useState<DataCDROverviewType[]>([])
  const [detailedRows, setDetailedRows] = useState<DataCDRPackageDetailType[]>(
    []
  )
  const [remainingData, setRemainingData] = useState('')

  const {
    data: overviewRowsData,
    isLoading: isOverviewCRDDataLoading,
    refetch: refetchCDROverview
  } = useQuery(
    ['getOverviewCRDData', row.iccid],
    async () => {
      const getCDROverviewDataResponse = await DataApi.getDataCDROverview(
        row.iccid,
        faberToken
      )
      return getCDROverviewDataResponse.data
    },
    {
      onSuccess: (res) => {
        if (res.data) {
          const remaining = res.totalDataAllocatedMB - res.totalDataUsedMB
          setRemainingData(remaining.toString())
          setOverviewRows(res.data)
        } else {
          setOverviewRows([])
        }
      },
      onError: () => {
        setOverviewRows([])
        toast.error('Error getting CDR data')
      },
      enabled: !!rolloverDialog
    }
  )

  const { isLoading: isDetailedCDRLoading, refetch: refetchDetailCDRData } =
    useQuery(
      ['getDetailDataCDR', row.iccid],
      async () => {
        const getCDRDataDetailsResponse =
          await DataApi.getDataCDRDetailedRecords(row.iccid, faberToken)
        return getCDRDataDetailsResponse?.data
      },
      {
        onSuccess: (res) => {
          if (res.data) {
            setDetailedRows(res.data)
          } else {
            setDetailedRows([])
          }
        },
        onError: () => {
          setDetailedRows([])
          toast.error('Error getting CDR data')
        },
        enabled: !!rolloverDialog
      }
    )
  const allMonths = useMemo(() => {
    const months = [new Date(row.subscriptionStartAt)]
    for (let i = 0; i < Number(row.cycleCount); i += 1) {
      const lastMonth = months[months.length - 1]
      months.push(addMonths(lastMonth, 1))
    }

    return months
  }, [row.subscriptionStartAt])

  useEffect(() => {
    if (rolloverDialog) {
      refetchCDROverview()
      refetchDetailCDRData()
    }
  }, [rolloverDialog])

  useEffect(() => {
    if (cycleInterval && overviewRowsData?.data) {
      const selectedDatesRows = overviewRowsData?.data.filter((row) => {
        return isWithinInterval(new Date(row.Timestamp), {
          start: new Date(cycleInterval),
          end: addMonths(new Date(cycleInterval), 1)
        })
      })
      setOverviewRows(selectedDatesRows)
    } else if (overviewRowsData?.data) {
      setOverviewRows(overviewRowsData.data)
    }
  }, [cycleInterval, overviewRowsData?.data])

  return (
    <Dialog
      open={rolloverDialog}
      onClose={() => {
        handleDialogClose()
        handleMenuClose()
      }}
      fullWidth={true}
      maxWidth='lg'
    >
      <DialogTitle
        sx={{
          margin: '0',
          fontWeight: '700',
          color: 'black',
          fontSize: '21px',
          mt: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>Data Allocation</Box>
        <IconButton
          onClick={handleDialogClose}
          size='small'
          data-cy='closeDialogBtn'
          sx={{
            mr: '-10px',
            mt: '-10px'
          }}
        >
          <img
            style={{
              width: '20px',
              height: '20px'
            }}
            src={MenuClose}
          />
        </IconButton>
      </DialogTitle>
      <Divider sx={{ width: '100%' }} />
      <DialogContent sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <TextColorFontBody
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: '#818D9C',
              mt: '15px'
            }}
          >
            Current cycle
          </TextColorFontBody>
        </Box>
        <Box sx={{ width: '30%' }}>
          <FormControl fullWidth>
            <InputLabel id='demo-simple-select-label'>Cycle Date</InputLabel>
            <Select
              defaultValue=''
              labelId='demo-simple-select-label'
              id='demo-simple-select'
              value={cycleInterval}
              label='Cycle Count'
              onChange={handleChange}
              endAdornment={
                cycleInterval ? (
                  <InputAdornment position='end' sx={{ mr: 2 }}>
                    <IconButton
                      onClick={() => {
                        setCycleInterval('')
                      }}
                    >
                      <HighlightOffIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            >
              {allMonths.map(
                (month, index) =>
                  index !== allMonths.length - 1 && (
                    <MenuItem
                      key={`month-${index}`}
                      value={format(month, 'd MMM yyyy')}
                    >
                      {`${format(month, 'd MMM yyyy')} - ${format(
                        addMonths(month, 1),
                        'd MMM yyyy'
                      )}`}
                    </MenuItem>
                  )
              )}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      {!isOverviewCRDDataLoading && !isDetailedCDRLoading ? (
        <>
          <DialogContent sx={{ width: '100%', padding: '0' }}>
            <Box
              sx={{
                backgroundColor: '#F9F9F9',
                pl: '24px',
                pr: '40px',
                display: 'flex',
                justifyContent: 'space-between'
              }}
              display='grid'
            >
              <DetailsPaneItem
                header='Cycle Start Date'
                content={format(
                  addMonths(
                    new Date(row.subscriptionStartAt),
                    Number(row.cycleCount) - 1
                  ),
                  'dd MMM yyyy HH:mm'
                )}
              />
              <DetailsPaneItem header='Current Plan' content={row.planName} />
              {/* TO DO: to fill in when data cdr api is up */}
              <DetailsPaneItem
                header='Total Data Allocated (MB)'
                content={
                  overviewRowsData?.totalDataAllocatedMB
                    ? overviewRowsData?.totalDataAllocatedMB.toString()
                    : ''
                }
              />
              <DetailsPaneItem
                header='Total Data Usage (MB)'
                content={
                  overviewRowsData?.totalDataAllocatedMB
                    ? overviewRowsData?.totalDataUsedMB.toString()
                    : ''
                }
              />
              <DetailsPaneItem
                header='Total Data Remaining (MB)'
                content={remainingData}
              />
            </Box>
          </DialogContent>

          <DialogContent>
            <TextColorFontBody
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#818D9C',
                marginBottom: '24px'
              }}
            >
              Data Allocation Log
            </TextColorFontBody>

            <DataAllocationLog
              overviewRows={overviewRows}
              detailedRows={detailedRows}
            />
          </DialogContent>
        </>
      ) : (
        <DialogContent sx={{ width: '100%', padding: '0', height: '20vh' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      )}
    </Dialog>
  )
}

export interface RolloverLogProps {
  transactionRows: FinanceTableRows[]
}

interface FaberDataRolloverDialogProps {
  handleDialogClose: () => void
  rolloverDialog: boolean
  handleMenuClose: (value: void) => void
  row: DataRolloverRows
}

export default FaberCDRDialog
